import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const AttendeeManagement = () => {
  const [activeTab, setActiveTab] = useState('engagement');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sample data
  const engagementHistory = [
    { id: 1, no: 1, date: 'Apr 1', eventName: 'Open Praise', activityType: 'Event Reviewed', coinsEarned: '10 coins' },
    { id: 2, no: 2, date: 'Apr 12', eventName: 'Referred a friend', activityType: 'Referred a friend', coinsEarned: '10 coins' },
    { id: 3, no: 3, date: 'Apr 15', eventName: 'Tech Conference', activityType: 'Ticket Purchase', coinsEarned: '5 coins' },
    { id: 4, no: 4, date: 'Apr 18', eventName: 'Live Concert', activityType: 'Event Reviewed', coinsEarned: '10 coins' }
  ];

  const bookingHistory = [
    { 
      id: 1, 
      eventName: 'Nigeria Female Entrepreneurs Conference', 
      bookingId: '12603696', 
      bookingDate: '27-Mar-2025', 
      bookedFor: 'Oluch Davies', 
      venue: 'Online Event',
      image: "FestivalImage.png"
    },
    { 
      id: 2, 
      eventName: 'Nigeria Female Entrepreneurs Conference', 
      bookingId: '12603697', 
      bookingDate: '27-Mar-2025', 
      bookedFor: 'Oluch Davies', 
      venue: 'Online Event',
      image: "FestivalImage.png"
    },
    { 
      id: 3, 
      eventName: 'Nigeria Female Entrepreneurs Conference', 
      bookingId: '12603698', 
      bookingDate: '27-Mar-2025', 
      bookedFor: 'Oluch Davies', 
      venue: 'Online Event',
      image: "FestivalImage.png"
    }
  ];

  const filteredEngagementHistory = engagementHistory.filter(item =>
    item.eventName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.activityType.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredBookings = bookingHistory.filter(booking =>
    booking.eventName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    booking.bookingId.includes(searchQuery) ||
    booking.bookedFor.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle view details
  const handleViewDetails = (id) => {
    setSelectedBooking(id === selectedBooking ? null : id);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 bg-[#F5F5F5] min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6"
        >
          <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-[#06044B]">Attendee Management</h1>
          <p className="text-gray-500 mt-1 text-sm md:text-base">
            View and manage attendee engagement and booking history
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6 overflow-x-auto scrollbar-thin">
          <button
            onClick={() => setActiveTab('engagement')}
            className={`px-4 py-2 text-sm font-medium relative whitespace-nowrap ${activeTab === 'engagement' ? 'text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Engagement History
            {activeTab === 'engagement' && (
              <motion.div 
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              />
            )}
          </button>
          <button
            onClick={() => setActiveTab('booking')}
            className={`px-4 py-2 text-sm font-medium relative whitespace-nowrap ${activeTab === 'booking' ? 'text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Booking History
            {activeTab === 'booking' && (
              <motion.div 
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              />
            )}
          </button>
        </div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search ${activeTab === 'engagement' ? 'engagement history' : 'bookings'}...`}
              className="block w-full h-[44px] pl-10 pr-3 py-2 border border-gray-300 rounded-[50px] leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className='w-full'
          >
            {/* Engagement History Tab */}
            {activeTab === 'engagement' ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 w-full"
              >
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-lg md:text-xl font-medium text-[#1E1E1E]">Engagement history</h2>
                  <div className="flex items-center space-x-2">
                    <select className="text-sm border border-gray-300 rounded-md p-1 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                      <option>All Activities</option>
                      <option>Event Reviewed</option>
                      <option>Referred a friend</option>
                      <option>Ticket Purchase</option>
                    </select>
                  </div>
                </div>
                
                {/* Desktop View */}
                <div className="overflow-x-auto hidden md:block">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs md:text-sm font-medium text-[#1E1E1E] uppercase tracking-wider">No</th>
                        <th className="px-6 py-3 text-left text-xs md:text-sm font-medium text-[#1E1E1E] uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs md:text-sm font-medium text-[#1E1E1E] uppercase tracking-wider">Event Name</th>
                        <th className="px-6 py-3 text-left text-xs md:text-sm font-medium text-[#1E1E1E] uppercase tracking-wider">Activity Type</th>
                        <th className="px-6 py-3 text-left text-xs md:text-sm font-medium text-[#1E1E1E] uppercase tracking-wider">Coins Earned</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredEngagementHistory.length > 0 ? (
                        filteredEngagementHistory.map((item) => (
                          <motion.tr
                            key={item.id}
                            whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
                            className="transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#1E1E1E]">{item.no}.</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#1E1E1E]">{item.date}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#1E1E1E]">{item.eventName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#1E1E1E]">{item.activityType}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-yellow-600">{item.coinsEarned}</td>
                          </motion.tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                            No engagement history found matching your search.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Mobile View */}
                <div className="md:hidden divide-y divide-gray-200">
                  {filteredEngagementHistory.length > 0 ? (
                    filteredEngagementHistory.map((item) => (
                      <motion.div 
                        key={item.id}
                        className="p-4 hover:bg-gray-50"
                        whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-[#1E1E1E] mr-2">{item.no}.</span>
                            <span className="text-sm text-[#1E1E1E]">{item.date}</span>
                          </div>
                          <span className="text-sm font-medium text-yellow-600">{item.coinsEarned}</span>
                        </div>
                        <h3 className="text-base font-medium text-[#1E1E1E] mb-1">{item.eventName}</h3>
                        <p className="text-sm text-gray-600">{item.activityType}</p>
                      </motion.div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      No engagement history found matching your search.
                    </div>
                  )}
                </div>
              </motion.div>
            ) : (
              /* Booking History Tab */
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="space-y-4 w-full"
              >
                {filteredBookings.length > 0 ? (
                  filteredBookings.map((booking) => (
                    <motion.div
                      key={booking.id}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`bg-white w-full rounded-xl shadow-sm p-4 border border-gray-100 ${selectedBooking === booking.id ? 'ring-2 ring-indigo-500' : ''}`}
                    >
                      <div className='flex flex-col md:flex-row md:items-center'>
                        <img 
                          src={booking.image} 
                          alt={booking.eventName} 
                          className="w-full h-36 md:w-20 md:h-20 mb-4 md:mb-0 md:mr-5 rounded-md object-cover"
                        />
                        <div className="flex-1 space-y-4 md:space-y-0">
                          <h3 className="text-base md:text-lg font-semibold text-[#1E1E1E] mb-2">{booking.eventName}</h3>
                          
                          <div className="flex flex-col md:flex-row md:justify-between md:items-center w-full gap-4">
                            {/* Left Section */}
                            <div className="space-y-2">
                              <div className='flex'>
                                <p className="text-gray-500 mr-1">Booking ID: </p>
                                <p className="font-medium">{booking.bookingId}</p>
                              </div>
                              <div className='flex'>
                                <p className="text-gray-500 mr-1">Booking on: </p>
                                <p className="font-medium">{booking.bookingDate}</p>
                              </div>
                            </div>

                            {/* Middle Section */}
                            <div className="space-y-2">
                              <div className='flex'>
                                <p className="text-gray-500 mr-1">Booked for: </p>
                                <p className="font-medium">{booking.bookedFor}</p>
                              </div>
                              <div className='flex'>
                                <p className="text-gray-500 mr-1">Venue: </p>
                                <p className="font-medium">{booking.venue}</p>
                              </div>
                            </div>

                            {/* Right Section - Button */}
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleViewDetails(booking.id)}
                              className="bg-[#F8F8F8] h-[44px] md:h-[58px] w-full md:w-[156px] text-[#7F7F7F] border border-[#E3E3E3] px-4 py-2 rounded-[32px] font-normal text-sm md:text-[18px] mt-4 md:mt-0"
                            >
                              {selectedBooking === booking.id ? 'Hide Details' : 'View Details'}
                            </motion.button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded Details */}
                      <AnimatePresence>
                        {selectedBooking === booking.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mt-4 pt-4 border-t border-gray-100 overflow-hidden"
                          >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <h4 className="font-medium text-sm text-gray-700 mb-2">Attendee Details</h4>
                                <p className="text-sm">Name: {booking.bookedFor}</p>
                                <p className="text-sm">Email: example@email.com</p>
                                <p className="text-sm">Phone: +1234567890</p>
                              </div>
                              <div>
                                <h4 className="font-medium text-sm text-gray-700 mb-2">Ticket Information</h4>
                                <p className="text-sm">Type: Standard Access</p>
                                <p className="text-sm">Price: $19.99</p>
                                <p className="text-sm">Status: Confirmed</p>
                              </div>
                              <div>
                                <h4 className="font-medium text-sm text-gray-700 mb-2">Additional Info</h4>
                                <p className="text-sm">Payment: Credit Card</p>
                                <p className="text-sm">Purchase Date: {booking.bookingDate}</p>
                              </div>
                            </div>
                            <div className="mt-4 flex justify-end space-x-2">
                              <button className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm">Send Message</button>
                              <button className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-md text-sm">Download Ticket</button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500 bg-white rounded-xl shadow-sm">
                    No bookings found matching your search.
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
        
        {/* Pagination */}
        <div className="mt-6 flex justify-center">
          <nav className="inline-flex shadow-sm rounded-md overflow-hidden">
            <button className="px-3 py-1 bg-white border border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50" disabled>
              Previous
            </button>
            <button className="px-3 py-1 bg-indigo-600 border border-indigo-600 text-sm font-medium text-white hover:bg-indigo-700">
              1
            </button>
            <button className="px-3 py-1 bg-white border border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50">
              2
            </button>
            <button className="px-3 py-1 bg-white border border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50">
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default AttendeeManagement;